import { generateModalConfig } from '@/components/TransactionModal/config/modalConfig';
import {
  ModalState,
  PaymentMethod,
  TransactionState,
} from '@/components/TransactionModal/type/ModalTypes';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

const initialState: ModalState = {
  config: {
    amount: false,
    remark: false,
    saveOption: false,
    paymentMethodOption: false,
    adminOption: false,
  },
  formStep: 0,
  error: undefined,
  transaction: {
    type: 'NONE',
    showOTPVerification: false,
    showWhereToSaveTransaction: false,
    maxAmount: undefined,
    admins: null,
    transactionId: 0,
    date: null,
    amount: 0,
  },
};

const modalSlice = createSlice({
  name: 'modal',
  initialState,
  reducers: {
    setTxState: (
      state: ModalState,
      { payload }: PayloadAction<TransactionState>,
    ) => {
      state.transaction = payload;
      state.config = generateModalConfig(payload);
    },
    setAmount: (state: ModalState, { payload }: PayloadAction<number>) => {
      state.transaction.amount = payload;
    },
    setRemark: (state: ModalState, { payload }: PayloadAction<string>) => {
      state.transaction.remark = payload;
    },
    setSaveOption: (state: ModalState, { payload }: PayloadAction<boolean>) => {
      state.transaction.saveInOriginalCaisse = payload;
    },
    setPaymentMethod: (
      state: ModalState,
      { payload }: PayloadAction<PaymentMethod>,
    ) => {
      state.transaction.methodPayment = payload;
    },
    setVerificationCode: (
      state: ModalState,
      { payload }: PayloadAction<string>,
    ) => {
      state.transaction.verificationCode = payload;
    },
    setVerificationUser: (
      state: ModalState,
      { payload }: PayloadAction<string>,
    ) => {
      state.transaction.verificationUser = payload;
    },
    setAdminId: (state: ModalState, { payload }: PayloadAction<number>) => {
      state.transaction.adminId = payload;
    },
    setDate: (state: ModalState, { payload }: PayloadAction<Date | null>) => {
      state.transaction.date = payload;
    },
    setNextStep: (state: ModalState) => {
      state.formStep += 1;
    },
    decreaseAttempt: (
      state: ModalState,
      { payload }: PayloadAction<string>,
    ) => {
      if (state.config.remainingAttempt !== undefined) {
        state.config.remainingAttempt -= 1;
        state.error = payload;
        if (state.config.remainingAttempt < 0) state.formStep = 2;
      }
    },
    setError: (
      state: ModalState,
      { payload }: PayloadAction<string | undefined>,
    ) => {
      state.error = payload;
    },
    resetModalState: (state: ModalState) => {
      Object.assign(state, initialState);
    },
  },
});

export const getInitialState = createSelector(
  ({ modal }: { modal: ModalState }) => modal.transaction.type,
  ({ modal }: { modal: ModalState }) => modal.transaction.showOTPVerification,
  ({ modal }: { modal: ModalState }) =>
    modal.transaction.showWhereToSaveTransaction,
  ({ modal }: { modal: ModalState }) => modal.transaction.maxAmount,
  ({ modal }: { modal: ModalState }) => modal.transaction.admins,
  ({ modal }: { modal: ModalState }) => modal.transaction.transactionId,
  (type, showOtp, showSaveOpt, maxAmount, admins, txId) => ({
    type,
    showOtp,
    showSaveOpt,
    maxAmount,
    admins,
    txId,
  }),
);

export const getCurrentFormStep = createSelector(
  (state: { modal: ModalState }) => state.modal,
  ({ formStep }) => formStep,
);

export const getAmount = createSelector(
  (state: { modal: ModalState }) => state.modal.transaction,
  ({ amount }) => amount,
);

export const getSaveOpt = createSelector(
  (state: { modal: ModalState }) => state.modal.transaction,
  ({ saveInOriginalCaisse }) => saveInOriginalCaisse,
);

export const getRemark = createSelector(
  (state: { modal: ModalState }) => state.modal.transaction,
  ({ remark }) => remark,
);

export const getVerificationCode = createSelector(
  (state: { modal: ModalState }) => state.modal.transaction,
  ({ verificationCode }) => verificationCode,
);

export const getVerificationUser = createSelector(
  (state: { modal: ModalState }) => state.modal.transaction,
  ({ verificationUser }) => verificationUser,
);

export const getSelectedAdminId = createSelector(
  (state: { modal: ModalState }) => state.modal.transaction,
  ({ adminId }) => adminId,
);

export const getSelectedPaymentMethod = createSelector(
  (state: { modal: ModalState }) => state.modal.transaction,
  ({ methodPayment }) => methodPayment,
);

export const getError = createSelector(
  (state: { modal: ModalState }) => state.modal,
  ({ error }) => error,
);

export const getModalConfig = createSelector(
  (state: { modal: ModalState }) => state.modal,
  ({ config }) => config,
);

export const getDate = createSelector(
  (state: { modal: ModalState }) => state.modal.transaction,
  ({ date }) => date,
);

export const {
  setTxState,
  setAmount,
  setRemark,
  setSaveOption,
  setPaymentMethod,
  setVerificationCode,
  setVerificationUser,
  setAdminId,
  setNextStep,
  setError,
  resetModalState,
  decreaseAttempt,
  setDate,
} = modalSlice.actions;

export default modalSlice.reducer;
